import type { Patient, PatientDTO, TermsAndConditions, TermsAndConditionsDTO } from "@akina/domain";
import type { Tenant } from "../auth";
import { patientMapper, termsAndConditionsMapper } from "../mappers";
import { ApiMethod, uploadFileToS3, type ApiConfiguration } from "./shared";

export const patientApi = (config: ApiConfiguration) => ({
  async get(): Promise<Patient> {
    const result = await config.httpService.fetch<PatientDTO>({
      method: ApiMethod.GET,
      url: config.endpoints.patients.get,
    });

    return patientMapper.toDomain(result);
  },
  async update(patient: Patient, signal?: AbortSignal): Promise<Patient> {
    await config.httpService.fetch<PatientDTO>({
      method: ApiMethod.PATCH,
      url: config.endpoints.patients.update,
      data: patientMapper.toDTO(patient),
      signal,
    });

    return await this.get();
  },
  async updateProfilePicture(newProfilePicture: File): Promise<void> {
    const result = await config.httpService.fetch<{ presigned_url: string }>({
      method: ApiMethod.PATCH,
      url: config.endpoints.patients.profilePicture.uploadUrl,
      data: { file_type: newProfilePicture.type },
    });

    await uploadFileToS3(result.presigned_url, newProfilePicture);
  },
  async deleteProfilePicture(): Promise<void> {
    await config.httpService.fetch<string>({
      method: ApiMethod.DELETE,
      url: config.endpoints.patients.profilePicture.delete,
    });
  },
  async getTermsAcceptedStatus(): Promise<TermsAndConditions> {
    const response = await config.httpService.fetch<TermsAndConditionsDTO>({
      method: ApiMethod.GET,
      url: config.endpoints.patients.getTermsAndConditions,
    });
    return termsAndConditionsMapper.toDomain(response);
  },
  async emailAlreadyExists(email: string, tenant: Tenant): Promise<boolean> {
    try {
      const response = await config.httpService.fetch<{ exists: boolean }>({
        method: ApiMethod.GET,
        url: config.endpoints.patients.emailAlreadyExists(email, tenant),
      });
      return response.exists;
    } catch (_) {
      return false;
    }
  },
  async updateTermsAcceptedStatus(status: TermsAndConditions): Promise<void> {
    await config.httpService.fetch({
      method: ApiMethod.POST,
      url: `${config.endpoints.patients.acceptTermsAndConditions}`,
      data: termsAndConditionsMapper.toDTO(status),
    });
  },
});
